import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useProjectSchedulesContext } from "@/modules/projects/components/schedules/ProjectSchedulesContext";
import { ProjectSchedulesVisitDetails } from "@/modules/projects/components/schedules/ProjectSchedulesVisitDetails";
import {
  ReadProjectSchedule,
  ReadProjectScheduleVisit,
} from "@/types/projectSchedule";
import { memo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useGetSchedulesContractorVisitQuery } from "../../hooks/useGetSchedulesContractorVisitQuery";
import Spinner from "@/components/Spinner";
import { ProjectSchedulesVisitTimeDetails } from "./ProjectSchedulesVisitTimesDetails";
import { PopoverClose } from "@radix-ui/react-popover";
import ConfirmDialog from "@/components/confirm-dialog";
import { useCancelScheduleVisitMutation } from "./hooks/useCancelScheduleVisitMutation";
import {
  VISIT_STATUS_NEW,
  VISIT_STATUS_COMPLETED,
} from "@/schemas/contractorVisit";
import { XCircle } from "lucide-react";

interface ProjectSchedulesVisitPreviewProps {
  projectSchedule: ReadProjectSchedule;
  projectScheduleVisit: ReadProjectScheduleVisit;
}

const ProjectSchedulesVisitPreviewComponent = ({
  projectSchedule,
  projectScheduleVisit,
}: ProjectSchedulesVisitPreviewProps) => {
  const { t } = useTranslation();
  const { projectId } = useProjectSchedulesContext();

  const { data: visit, isLoading: isContractorVisitLoading } =
    useGetSchedulesContractorVisitQuery(
      projectId,
      projectSchedule.id,
      projectScheduleVisit.id,
    );

  const cancelMutation = useCancelScheduleVisitMutation(
    projectSchedule.id,
    projectScheduleVisit.id,
  );

  const handleCancel = useCallback(() => {
    cancelMutation.mutate(undefined);
  }, [cancelMutation]);

  if (isContractorVisitLoading) {
    return <Spinner />;
  }

  const disabled = isContractorVisitLoading;
  const canCancel =
    projectScheduleVisit.status === VISIT_STATUS_NEW ||
    projectScheduleVisit.status === VISIT_STATUS_COMPLETED;
  const isPending = cancelMutation.isPending;

  return (
    <div className="flex flex-col gap-3 min-h-0">
      <ProjectSchedulesVisitTimeDetails visit={visit} />
      <Separator />
      <ProjectSchedulesVisitDetails visit={visit} />
      <Separator />
      <div className="flex flex-row gap-2 justify-end">
        {canCancel && (
          <ConfirmDialog
            title={t("projectContractorVisits.actions.cancel")}
            content={t("projectContractorVisits.actions.cancelConfirmContent")}
            onConfirm={handleCancel}
          >
            <Button variant="outline" disabled={disabled || isPending}>
              <XCircle className="h-4 w-4 mr-2" />
              {t("projectContractorVisits.actions.cancel")}
            </Button>
          </ConfirmDialog>
        )}
        <PopoverClose asChild>
          <Button variant="outline" disabled={disabled || isPending}>
            {t("common.close")}
          </Button>
        </PopoverClose>
      </div>
    </div>
  );
};

export const ProjectSchedulesVisitPreview = memo(
  ProjectSchedulesVisitPreviewComponent,
);
